import { theme } from '@contrast-security-inc/design-system-foundations'
import { css } from '@emotion/css'
import React from 'react'
import { useIntl } from 'react-intl'
import { GridType } from '../../../helpers/ui-interfaces'
import { getApplicationLibraryClassUsageCount } from '../../../helpers/get-application-library-class-usage-count'
import { DrilldownLibrary } from '../../../helpers/drilldown-ui-interfaces'
import { ScoreSection } from './score-section'
import { Stat } from './stat'
import { styles } from './stat/styles'

interface Props {
  library: DrilldownLibrary
  applicationID?: string
  navigateToUsageTab: () => void
  gridType: GridType
  isPrivate: boolean
}

export const SummaryBar = ({
  library,
  applicationID,
  navigateToUsageTab,
  gridType,
  isPrivate
}: Props) => {
  const { formatMessage } = useIntl()

  const { usage, vulnerabilities, grade, policies, applications } = library

  const getPolicyViolationCount = () => {
    let policyViolationCount = 0

    if (policies.restrictedByOrg) {
      policyViolationCount += 1
    }

    if (policies.restrictedLicense) {
      policyViolationCount += 1
    }

    if (policies.invalidVersion) {
      policyViolationCount += 1
    }

    return policyViolationCount
  }

  const isMergedApp =
    library.mergedApplications && library.mergedApplications.length > 0
  let classUsage = isMergedApp
    ? undefined
    : getApplicationLibraryClassUsageCount({
        applicationID,
        library: {
          libraryClassUsageCounts: library.libraryClassUsageCounts
        }
      })

  classUsage =
    // eslint-disable-next-line eqeqeq
    classUsage != undefined ? classUsage : usage.totalUsed

  return (
    <div
      className={css`
        background: ${theme.color.white};
        border: 1px solid #eee;
        box-shadow: 1px 1px 1px 0 rgba(0, 0, 0, 0.03);
        border-radius: 4px;
        margin-top: 18px;
        padding: 20px;
        display: flex;
        height: 130px;
        align-items: center;
      `}
    >
      <ScoreSection grade={grade} isPrivate={isPrivate} />
      <div
        className={css`
          display: flex;
          align-items: center;
          width: 100%;
          justify-content: space-around;
        `}
      >
        <Stat
          isDisabled={isPrivate}
          testId="summary_vulnerabilities"
          name={formatMessage({
            id:
              vulnerabilities.length === 1
                ? 'title_vulnerability'
                : 'title_vulnerabilities'
          })}
          value={isPrivate ? '?' : vulnerabilities.length}
        />
        <Stat
          testId="summary_violations"
          name={formatMessage({
            id:
              getPolicyViolationCount() === 1
                ? 'title_policyViolation'
                : 'title_policyViolations'
          })}
          value={getPolicyViolationCount()}
        />
        <Stat
          testId="summary_appsUsing"
          name={formatMessage({ id: 'title_libraryGrid_appsUsing' })}
          value={applications ? applications.length : 0}
        />
        <Stat
          testId="summary_usage"
          name={formatMessage({ id: 'title_libraryUsage_classesUsed' })}
          value={
            <div>
              {gridType === 'Application' ? classUsage : usage.totalUsed}
              <span
                className={styles.StatTotal(
                  applicationID !== undefined ? true : false
                )}
              >
                /{usage.total}
              </span>
            </div>
          }
          displayAsLink={applicationID !== undefined ? true : false}
          onClick={() => {
            if (applicationID) {
              navigateToUsageTab()
            }
          }}
        />
      </div>
    </div>
  )
}
